// Orbit 디자인 시안의 grad/mono 시각 요소를
// frontend에서 결정론적으로 생성하기 위한 헬퍼.
// backend는 utils.apps의 메타만 노출하고, 카드 그라데이션과 약자는 prefix/display_name에서 파생한다.

export interface ProjectVisualInput {
  prefix: string;
  display_name?: string | null;
}

// orbit mock에 등장하는 12개 팔레트. shared.jsx의 grad 값 그대로.
// 8~12개 범위에서 시각 다양성을 확보하면서 prefix 해시로 결정론적 분배.
const PALETTES: string[] = [
  'linear-gradient(135deg,#FF7A3D 0%,#F10EE2 50%,#6B1BFF 100%)',
  'linear-gradient(135deg,#1AAFFF 0%,#6B1BFF 60%,#F10EE2 100%)',
  'linear-gradient(135deg,#2A2A2A 0%,#6B1BFF 50%,#1AAFFF 100%)',
  'linear-gradient(135deg,#1AAFFF 0%,#6BD5FF 50%,#A5F1A5 100%)',
  'linear-gradient(135deg,#F10EE2 0%,#FF7AC0 50%,#FFD86B 100%)',
  'linear-gradient(135deg,#FFD86B 0%,#FF7A3D 50%,#CA4439 100%)',
  'linear-gradient(135deg,#6BA66F 0%,#1AAFFF 60%,#6B1BFF 100%)',
  'linear-gradient(135deg,#A5F1A5 0%,#1AAFFF 60%,#6B1BFF 100%)',
  'linear-gradient(135deg,#FFD86B 0%,#6BA66F 50%,#1AAFFF 100%)',
  'linear-gradient(135deg,#FF7AC0 0%,#F10EE2 50%,#6B1BFF 100%)',
  'linear-gradient(135deg,#FFD86B 0%,#A5F1A5 50%,#6BD5FF 100%)',
  'linear-gradient(135deg,#F10EE2 0%,#6B1BFF 50%,#1AAFFF 100%)',
];

// 결정론적 32-bit 문자열 해시. djb2 변종 — 짧은 prefix에서도 충돌 분포 양호.
function hashString(input: string): number {
  let hash = 5381;
  for (let i = 0; i < input.length; i += 1) {
    // 부호 비트 회피를 위해 unsigned shift.
    hash = ((hash << 5) + hash + input.charCodeAt(i)) >>> 0;
  }
  return hash >>> 0;
}

// prefix를 PALETTES 인덱스로 매핑. 같은 prefix는 항상 같은 그라데이션.
export function projectGrad(prefix: string): string {
  if (!prefix) return PALETTES[0];
  const idx = hashString(prefix) % PALETTES.length;
  return PALETTES[idx];
}

// display_name이 있으면 단어 첫 글자 2개로, 없으면 prefix 첫 2글자로 약자 생성.
// orbit mock의 mono와 동일 의미 — 화려한 카드 정중앙에 띄우는 큰 약자.
export function projectMono(input: ProjectVisualInput): string {
  const name = (input.display_name ?? '').trim();
  if (name) {
    // 공백/대시/언더스코어로 단어 분리 → 각 첫 글자.
    const tokens = name.split(/[\s\-_]+/).filter(Boolean);
    if (tokens.length >= 2) {
      return (tokens[0][0] + tokens[1][0]).toUpperCase();
    }
    // 한 단어이면 앞 두 글자.
    if (tokens.length === 1) {
      const t = tokens[0];
      return t.slice(0, 2).toUpperCase();
    }
  }
  const prefix = (input.prefix ?? '').trim();
  if (!prefix) return '??';
  return prefix.slice(0, 2).toUpperCase();
}
